import { createContext, useContext, type AnchorHTMLAttributes } from "react";
import { Link as RouterLink, NavLink as RouterNavLink, type LinkProps, type NavLinkProps, useLocation } from "react-router-dom";
import { Globe2 } from "lucide-react";
import { languageNames, ui } from "./content/locales";
import { articlesFor, findArticle, translations } from "./content/articles";
import { locales, type MarketingLocale } from "./content/types";
import { articlePath, localePath, localizeLink, parsePathname } from "./routing";

export const LocaleContext = createContext<MarketingLocale>("el");
export const useLocale = () => useContext(LocaleContext);
const adminOrigin = (import.meta.env.VITE_ADMIN_URL || "https://admin.trykate.app").replace(/\/$/, "");
export function AdminLink({ to, ...props }: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & { to: "/login" | "/register" }) {
  const locale = useLocale();
  return <a href={`${adminOrigin}${to}?lang=${locale === "el" ? "el" : "en"}`} {...props}/>;
}
export function Link({ to, ...props }: LinkProps) { const locale = useLocale(); return <RouterLink to={typeof to === "string" ? localizeLink(to, locale) : to} {...props}/>; }
export function NavLink({ to, ...props }: NavLinkProps) { const locale = useLocale(); return <RouterNavLink to={typeof to === "string" ? localizeLink(to, locale) : to} {...props}/>; }

export function languageDestination(pathname: string, target: MarketingLocale) {
  const current = parsePathname(pathname);
  const articleSlug = /^\/blog\/([^/]+)$/.exec(current.path)?.[1];
  const article = articleSlug ? findArticle(current.locale, articleSlug) : undefined;
  if (article) {
    const translated = translations(article).find(other => other.locale === target);
    return { path: translated ? articlePath(target, translated.slug) : localePath("/blog", target), equivalent: Boolean(translated) };
  }
  if (current.path.startsWith("/blog")) return { path: localePath("/blog", target), equivalent: current.path === "/blog" };
  if (current.path === "/" || target === "el" || target === "en") return { path: localePath(current.path, target), equivalent: current.locale === "el" || current.locale === "en" || current.path === "/" };
  return { path: localePath("/", target), equivalent: false };
}

export function LanguageSwitcher() {
  const locale = useLocale();
  const { pathname } = useLocation();
  return <details className="language-switcher"><summary aria-label={locale === "el" ? "Επιλογή γλώσσας" : "Language"}><Globe2/><span>{locale.toUpperCase()}</span></summary><div>
    {locales.filter(value => articlesFor(value).length > 0).map(value => {
      const destination = languageDestination(pathname, value);
      return <a key={value} href={destination.path} hrefLang={value} lang={value} aria-current={value === locale ? "true" : undefined} title={destination.equivalent ? languageNames[value] : ui[value].notTranslated}>{languageNames[value]}{value === locale && <span>✓</span>}</a>;
    })}
  </div></details>;
}
